import { Button, Input } from '@nextui-org/react'
import { useState } from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Icons } from '../icons'

import { AddressEditor } from './AddressEditor'

interface AddressControlProps<T extends FieldValues> {
    name: Path<T>
    control: Control<T>
    rules?: Record<string, any>
    placeholder?: string
    label?: string
    isRequired?: boolean
    isDisabled?: boolean
    className?: string
    department?: string
}

export function AddressControl<T extends FieldValues>({
    name,
    control,
    rules,
    placeholder,
    label,
    isRequired = false,
    isDisabled = false,
    className = '',
    department
}: AddressControlProps<T>): React.ReactElement {
    const { t } = useTranslation()
    const [isEditorOpen, setIsEditorOpen] = useState(false)

    const handleOpenEditor = () => {
        if (!isDisabled) {
            setIsEditorOpen(true)
        }
    }

    const handleCloseEditor = () => {
        setIsEditorOpen(false)
    }

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState: { error } }) => (
                <div className={className}>
                    <Input
                        {...field}
                        variant="bordered"
                        label={label}
                        placeholder={
                            placeholder
                                ? t(placeholder)
                                : t('address.enterAddress')
                        }
                        value={field.value || ''}
                        isInvalid={!!error}
                        errorMessage={error ? `${error.message}*` : ''}
                        isRequired={isRequired}
                        isDisabled={isDisabled}
                        readOnly
                        className={isDisabled ? '' : 'cursor-pointer'}
                        classNames={{
                            input: isDisabled ? '' : 'cursor-pointer',
                            inputWrapper: isDisabled ? '' : 'cursor-pointer'
                        }}
                        onClick={handleOpenEditor}
                        endContent={
                            <div className="flex items-center gap-2">
                                <Button
                                    isIconOnly
                                    variant="light"
                                    size="sm"
                                    onPress={handleOpenEditor}
                                    className="text-primary"
                                    isDisabled={isDisabled}
                                >
                                    <Icons.Location className="text-lg" />
                                </Button>
                            </div>
                        }
                    />

                    <AddressEditor
                        isOpen={isEditorOpen}
                        onClose={handleCloseEditor}
                        onSave={(address: string) => {
                            field.onChange(address)
                        }}
                        initialValue={field.value}
                        title="address.editTitle"
                        department={department}
                    />
                </div>
            )}
        />
    )
}
